import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import FolderIcon from '@mui/icons-material/Folder';
import FolderSharedIcon from '@mui/icons-material/FolderShared'; // cohort
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PushPinFilledIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount'; // team
import { Box, Button, Divider, IconButton, List, ListItem, ListItemIcon, ListItemText, Modal, Stack, SwipeableDrawer, Tooltip, Typography } from '@mui/material';
import { useAtom, useSetAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { DropdownV2 } from '../../components/assorted/dropdown-v2';
import { baseAtom, findParent, getUIStateIsOptionSelected, setLoadingOrg, setOrgToggle, setUIStateIsOptionSelected, softSelect, toggleOrgTreeItemExpanded, toggleSticky } from '../../stores/jotai/base';
import { categoryAtom } from '../../stores/jotai/categories/categories';

import { useTheme } from '@mui/material/styles';
import IwFullLogo from '../../components/app/IwFullLogo';
import ScoringRatingPanel from '../../components/scoring/ScoringRatingPanel';
import { INTERVIEW_PAGE_WIDTH_LIMITS, STAR_RATINGS_DISTRIBUTION } from '../../constants';
import { discoverySessionAtom, setSessionByGroupID, setSessionByOrgID } from '../../stores/jotai/discovery-session';
import { v3MigrationAtom } from '../../stores/jotai/migration/v3MigrationAtom';

import { teamsAtom } from '../../stores/jotai/teams/teams';
import { isInPresentationMode } from '../../util';
import { SAdminBaseAtom } from '../../stores/jotai/super-admin/superAdminBase';
import IwMenu from '../../components/app/IwMenu';
import MoreVertIcon from "@mui/icons-material/MoreVert";

const NEW_TEAM_UI_STATE_KEY = "org_tree_new_team";

const PUSH_PIN_ICON_STYLES = {
    color: "inherit !important",
    fontSize: "18px",
    transform: "rotate(40deg)",
    ":hover": { transform: "rotate(40deg)" },
};
const IwMenuCommonProps = {
    label: <MoreVertIcon fontSize="medium" />,
    tooltipProps: { title: "Options" },
    anchorOrigin: { vertical: "top", horizontal: "right" },
    transformOrigin: { vertical: "top", horizontal: "left" },
  } as const;

export default function MaterialOrgTree(props) {
    const {isSuperAdmin, orgTreeData, windowSize, isAdminPannel,setParentGroupID}=props

    const theme = useTheme();
    const parentPanelStyle = {
        overflow: 'visible',
        maxWidth: '300px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: theme.custom.darkestAccent,
    };

    const [base, setBase] = useAtom(baseAtom);
    const [categoryState, setCategoryState] = useAtom(categoryAtom);
    const [sAdminBase,setSAdminBase]=useAtom(SAdminBaseAtom);
    const [discoverySession, setDiscoverySession] = useAtom(discoverySessionAtom);
	  const setV3MigrationAtomState = useSetAtom(v3MigrationAtom);
    const [teams,setTeams]=useAtom(teamsAtom);

    const navigate = useNavigate();
    const location = useLocation();

    let cohortVisibility=false;
    let rollVisibility='';

    const [superAdminSelectedOrg, setSuperAdminSelectedOrg] = useState({
        value: base.org.name,
        key: base.org.orgID
    });
    
    // Effect to update selected value based on conditions
    React.useEffect(() => {
        if (isAdminPannel && sAdminBase?.selectedOrg?.orgID) {
            setSuperAdminSelectedOrg({
                value: sAdminBase?.selectedOrg?.name || "",
                key: sAdminBase?.selectedOrg?.orgID || ""
            });
        } else {
            setSuperAdminSelectedOrg({
                value: base.org.name,
                key: base.org.orgID
            });
        }
    }, [isAdminPannel, sAdminBase?.selectedOrg, base.org]);


    const OrgRollSnapShot = orgTreeData?.roles?.reduce((acc, roll) => {
        acc[roll.groupID] = roll.name;
        return acc;
    }, {});  

    const showNewTeam = base
        ? getUIStateIsOptionSelected(base, NEW_TEAM_UI_STATE_KEY)
        : false;

    const setNewTeamVisibility = (target: boolean) => {
        setUIStateIsOptionSelected(setBase, base, NEW_TEAM_UI_STATE_KEY, target);
    }
    
    const isInScoringPage = isInPresentationMode(location.pathname);

    const renderNewTeam = () => {
        return <Modal sx={{ zIndex: theme.custom.dialogZIndex }} open={showNewTeam}>
            {showNewTeam ? <span>We are working on it</span> : <span />}
        </Modal>
    };

    const prepareHierarchy = (items = []) => {
        const sortedList = [...items].filter((item) => item?.group?.name);

        sortedList.sort((a, b) => {
            const nameA = a.group?.name?.toLowerCase();
            const nameB = b.group?.name?.toLowerCase();

            if (nameA < nameB) {
                return -1;
            }
            if (nameA > nameB) {
                return 1;
            }

            return 0;
        });

        return sortedList;
    }

    const renderOrgTreeToggleButtonTooltip = () => {
        if (isInScoringPage) {
            return "Can't close this in Scoring page";
        }

        return null;
    };

    const renderCurrentParent = () => {
        const { cohort, selectedGroup } = props;
        const parentGroup = cohort;

        if (parentGroup === undefined) return <span></span>;

        const listItemStyle = {
            paddingLeft: '10px',
            paddingRight: '10px',
            display: 'flex',
            alignItems: "center",
            borderBottom: '1px solid white',
        }

        let newTeamWidget = undefined;

        return (
            <span style={{ display: "block" }}>
                <ListItem
                    style={listItemStyle}
                >
                    <Tooltip title="Show the Organization Structure">
                        <IconButton
                            sx={{
                                border: `1px solid ${theme?.custom?.white}`,
                                borderRadius: "4px",
                                padding: "4px",
                                marginRight: "4px",
                            }}
                            onClick={(e) => {
                                softSelect(setBase, undefined,orgTreeData?.flatHierarchy,teams);
                            }}
                        >
                            {/* <UndoIcon sx={{ color: 'white' }} /> */}
                            <ArrowBackIosNewOutlinedIcon
                                sx={{ color: "white", fontSize: "1rem" }}
                            />
                        </IconButton>
                    </Tooltip>
                    <div
                        style={{
                            display: "inline-block",
                            paddingLeft: "5px",
                            fontWeight: "400",
                            fontSize: "1rem",
                            lineHeight: "1.5",
                            letterSpacing: "0.00938em",
                        }}
                    >
                        { selectedGroup?.group?.groupType === "Cohort" && selectedGroup?.group?.name || parentGroup.name}
                    </div>
                    {newTeamWidget}
                    {/* <span onClick={(e) => { this.handleGroupSelect(parentGroup.id, true) }} style={{ display: 'inline-block', width: '80%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{parentGroup.name}</span>
                        */}
                </ListItem>
            </span>
        );
    }

    const renderHiearchyItem = (child, doesntHaveChildren, parentGroupID) => {
        let group = child;
        let clickable = false;

        //Admin panel based -according to shukrit-hayat
        let selectedVal = "";
        if (isAdminPannel) {
            clickable = true;
        }

        const { team } = props,
            key = `org-tree-item-${group.groupID}`,
            action = `manage-${group.type ? group.type.toLowerCase() : ""}`;


        

        if (group.groupType === "Team") {
            clickable = true;

            //Admin panel based -according to shukrit-hayat
            selectedVal = isSuperAdmin ? "Team" : "";
        }

        if (group.groupType === "Cohort") {
            if (doesntHaveChildren === false) {
                clickable = true;
            }

            //Admin panel based -according to shukrit-hayat
            selectedVal = isSuperAdmin ? "Cohort" : "";
        }

        return <ListItem sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            borderLeft: '3px solid white',
            paddingLeft: '10px',
            marginBottom: '5px',
            marginRight: '0px',
            paddingRight: '0px',
            cursor: clickable ? 'pointer' : 'disabled',
            borderRadius: '0px 4px 4px 0px',
            backgroundColor: team.groupID === group.groupID ? theme.custom.mainButtonHighlight : 'transparent',
            ':hover': {
                backgroundColor: team.groupID === group.groupID ? '#4791db' : '#374655',
            }
        }} onClick={(e) => {
            if (clickable) {
                if(isSuperAdmin){
                    // will activate after SAdmin code addition
                    // setAdminPanelAtom(setAdminPanelAtomState, selectedVal);
                    // setGroupAtom(setGroupAtomState, group);
                    if (!isAdminPannel) {
                        handleGroupClick(group.groupID);
                    }
                }
                else{
                   handleGroupClick(group.groupID)
                }
            }
        }}>
            <div style={{ minWidth: '32px' }}><SupervisorAccountIcon sx={{ color: 'white' }} /></div>
            <div><Typography>{group.name}</Typography></div>

            {/* {this.renderChatCountBadge(group, _cohort)} */}
        </ListItem>;
    }

    const renderNode = (i,node, offset, select) => {
        let nodes: any = [];

        const {group}=node;
 
        if (node.group) {
            const group = node.group;
            const type = group.groupType;
            const doesntHaveChildren = node.children === undefined || node.children.filter(c => c.group.name !== '-').length === 0;

            let icon = undefined;

            //Admin panel based -according to shukrit-hayat
            let selectedVal = "Folder";

            if (type === 'Cohort') {
                icon = <ListItemIcon sx={{ background: 'transparent', minWidth: '32px' }}>
                    {doesntHaveChildren ? <div style={{ width: '22.5px' }}></div> :
                        <IconButton sx={{ padding: '0px' }} onClick={(e) => {
                            e.stopPropagation()
                            toggleOrgTreeItemExpanded(setBase, { groupID: group.groupID, parentID: node.parentID }, !node.isExpanded,orgTreeData?.hierarchy)
                        }
                        }>
                            {node.isExpanded ? <KeyboardArrowDownIcon sx={{ color: 'white' }} /> : <KeyboardArrowRightIcon sx={{ color: 'white' }} />}
                        </IconButton>
                    }
                    <FolderSharedIcon sx={{ color: 'white', marginRight: '10px' }} />
                </ListItemIcon>
            }

            if (type === 'Team') {
                icon = <ListItemIcon sx={{ background: 'transparent', minWidth: '32px' }}>
                    <SupervisorAccountIcon sx={{ color: 'white' }} />
                </ListItemIcon>
            }

            if (type === 'Folder') {
                icon = <ListItemIcon sx={{ background: 'transparent', minWidth: '32px' }}>
                    {doesntHaveChildren ? <div style={{ width: '22.5px' }}></div> :
                        <IconButton sx={{ padding: '0px' }} onClick={(e) => {

                            //Admin panel based -according to shukrit-hayat 
                            if(isSuperAdmin){
                                // setAdminPanelAtom(setAdminPanelAtomState, "Folder");
                            }

                            e.stopPropagation()
                            toggleOrgTreeItemExpanded(setBase, { groupID: group.groupID }, !node.isExpanded,orgTreeData?.hierarchy)
                        }
                        }>
                            {node.isExpanded ? <KeyboardArrowDownIcon sx={{ color: 'white' }} /> : <KeyboardArrowRightIcon sx={{ color: 'white' }} />}
                        </IconButton>
                    }
                    <FolderIcon sx={{ color: 'white', marginRight: '10px' }} />
                </ListItemIcon>
            }

            let paddingCalculation = offset * 34;
            if (type === 'Cohort') {
                paddingCalculation = offset * 24
            }

            let clickable = false;

             //Admin panel based -according to shukrit-hayat
            if(isAdminPannel){
                clickable = true;
            }

            if (group.groupType === "Team") {
                clickable = true;

                //Admin panel based -according to shukrit-hayat
                selectedVal = isSuperAdmin ? "Team" : "";
            }

            if (group.groupType === "Cohort") {
                if (doesntHaveChildren === false) {
                    clickable = true;
                }

                //Admin panel based -according to shukrit-hayat
                selectedVal = isSuperAdmin ? "Cohort" : "";
            }

            if (!!group.name && group.name !== "-") {
              if (isSuperAdmin) {
                const isSelectedGroup = !isAdminPannel 
                  ? group?.groupID === base?.team?.groupID 
                  : group?.groupID === sAdminBase?.selectedGroup?.group?.groupID;

                const bgColor = isSelectedGroup ? theme.custom.mainButtonHighlight : 'transparent';
                const hoverBgColor = isSelectedGroup ? '#4791db' : '#374655';

                nodes.push(
                    <ListItem key={i}  sx={{
                        cursor: clickable ? 'pointer !important' : 'disabled !important',
                        maxWidth: '283px',
                        paddingLeft: paddingCalculation.toString() + "px",
                        width: '100%',
                        borderRadius: '4px',
                        paddingTop: '5px',
                        paddingBottom: '5px',
                        backgroundColor: bgColor,
                        ':hover': {
                            backgroundColor: hoverBgColor,
                        }
                    }} onClick={() => {
                        if (clickable) {
                            // setAdminPanelAtom(setAdminPanelAtomState, selectedVal);
                            // setGroupAtom(setGroupAtomState, node.group);
                            if (
                              node.group.groupType === "Team" &&
                              !isAdminPannel
                            ) {
                              handleGroupClick(node.group.groupID);
                            } else if (
                              node.group.groupType !== "Team" &&
                              !isAdminPannel
                            ) {
                              select(node);
                            } else if (
                              node.group.groupType === "Team" &&
                              isAdminPannel
                            ) {
                              const parentData = findParent(node?.group?.groupID, orgTreeData?.hierarchy);
                              setSAdminBase((prev) => {
                                return {
                                    ...prev,
                                    selectedCohort:parentData,
                                    selectedGroup:node,
                                };
                              });

                              navigate(`/admin/cohort/${node.parentID}/team/${node.group.groupID}`);
                            } else if (
                              node.group.groupType === "Cohort" &&
                              isAdminPannel
                            ) {
                              setSAdminBase((prev) => {
                                return {
                                    ...prev,
                                    selectedCohort:node,
                                    selectedGroup:null,
                                };
                              });

                              navigate(`/admin/cohort/${node.group.groupID}`);
                            }
                            
                        }
                    }}>
                        {icon}
                        <ListItemText sx={{ background: 'transparent', position: 'relative', top: '1px' }} primary={group.name} />
                        {
                            (location.pathname.includes("/admin") && node.group.groupType !== "Team" && node.group.groupType==="Folder") &&
                            <IwMenu
                            {...IwMenuCommonProps}
                            items={[
                              {
                                id: "add-cohort",
                                label: "Add Cohort",
                                onClick: () => {
                                   setParentGroupID(node.group.groupID)
                                   setSAdminBase(prev => ({...prev, addCohortModalOpen: true}));
                                },
                              },
                              {
                                id: "delete",
                                label: "Delete",
                                onClick: () => {
                                  setSAdminBase(prev => ({...prev, deleteGroupConfrimModalOpen: true,deleteGroupDataInfo:node}));
                                },
                              },
                            ]}
                          />
                        }
                        {
                            (location.pathname.includes("/admin") && node.group.groupType !== "Team" && node.group.groupType==="Cohort") &&
                            <IwMenu
                            {...IwMenuCommonProps}
                            items={[
                              {
                                id: "add-cohort",
                                label: "Add Team",
                                onClick: () => {
                                   setParentGroupID(node.group.groupID);
                                   setSAdminBase(prev => ({...prev, addTeamModalOpen: true}));
                                },
                              },
                              {
                                id: "delete",
                                label: "Delete",
                                onClick: () => {
                                  setSAdminBase(prev => ({...prev, deleteGroupConfrimModalOpen: true,deleteGroupDataInfo:node}));
                                },
                              },
                            ]}
                          />
                        }
                          {
                            (location.pathname.includes("/admin") && node.group.groupType === "Team") &&
                            <IwMenu
                            {...IwMenuCommonProps}
                            items={[
                              {
                                id: "delete",
                                label: "Delete",
                                onClick: () => {
                                  setSAdminBase(prev => ({...prev, deleteGroupConfrimModalOpen: true,deleteGroupDataInfo:node}));
                                },
                              },
                            ]}
                          />
                        }
                       
                    </ListItem>
                )
              } else {
                if (group?.groupType === "Cohort") {
                  cohortVisibility = group?.visibility === "closed";
                  rollVisibility = OrgRollSnapShot && OrgRollSnapShot[group?.groupID] ;
                }

                let shouldReturn = cohortVisibility ? false : true;
                let isTeam = group?.groupType === "Team";
                if (
                  [
                    "Admin",
                    "CohortAdmin",
                    "Instructor",
                    "Observer",
                    "SystemAdministrator",
                  ].includes(rollVisibility)
                ) {
                  isTeam = false;
                }

                if (isTeam) {
                    orgTreeData?.roles.forEach((role) => {
                    if (!shouldReturn && role.groupID === group.groupID) {
                      shouldReturn = true;
                    }
                  });
                }

                if (!isTeam || (isTeam && shouldReturn)) {
                  nodes.push(
                    <ListItem
                      key={`${group.groupID}-${Math.random()}-${i}`}
                      sx={{
                        cursor: clickable
                          ? "pointer !important"
                          : "disabled !important",
                        maxWidth: "283px",
                        paddingLeft: paddingCalculation.toString() + "px",
                        width: "100%",
                        borderRadius: "4px",
                        paddingTop: "5px",
                        paddingBottom: "5px",
                        backgroundColor: group.groupID === base.team.groupID ? theme.custom.mainButtonHighlight : 'transparent',
                        ":hover": {
                          backgroundColor:
                            group.groupID === base.team.groupID
                              ? "#4791db"
                              : "#374655",
                        },
                      }}
                      onClick={() => {
                        if (clickable) {
                          if (node.group.groupType === "Team") {
                            handleGroupClick(node.group.groupID);
                            //setLocalHide(true);
                          } else {
                            select(node);
                          }
                        }
                      }}
                    >
                      {icon}
                      <ListItemText
                        sx={{
                          background: "transparent",
                          position: "relative",
                          top: "1px",
                        }}
                        primary={group.name}
                      />
                    </ListItem>
                  );
                }
              }
              // }
            }
        }

        if (node.children && node.isExpanded) {
            const itemList = prepareHierarchy(node.children);

            for (let i = 0; i < itemList.length; i++) {
                let buffer = renderNode(i,itemList[i], offset + 1, select);

                for (let j = 0; j < buffer.length; j++) {
                    nodes.push(buffer[j]);
                }
            }
        }

        return nodes;
    }

    const handleGroupClick = (groupID) => {
        if (base.team.groupID !== groupID) {
            setSessionByGroupID({
                setBase,
                setCategoryState,
                set: setDiscoverySession,
                existing: discoverySession,
                groupID,
                statesWithPreviousValuesToHold: undefined,
                setV3MigrationAtomState,
            });
        }
    }

    const renderGroupSelector = () => {
      const itemList = prepareHierarchy(orgTreeData?.hierarchy);
      let children = [];

      for (let i = 0; i < itemList.length; i++) {
        let nodes = renderNode(i,itemList[i], 0, (g) => {
            if(!isSuperAdmin){
                softSelect(setBase, g,orgTreeData?.flatHierarchy,teams);
            }
        });

        for (let n = 0; n < nodes.length; n++) {
          children.push(<div key={`${itemList[i].group.groupID}-${n}`}>{nodes[n]}</div>);
        }
      }
      return (
        <div>
          {isSuperAdmin ? (
            <>
              <Button
                variant="contained"
                size="large"
                sx={{
                  cursor: "pointer !important",
                  maxWidth: "283px",
                  marginLeft: "8px",
                  width: "97%",
                  borderRadius: "4px",
                  paddingTop: "5px",
                  paddingBottom: "5px",
                  backgroundColor: theme.custom.mainButtonHighlight,
                }}
                onClick={() => {
                  const orgID =
                    isAdminPannel && sAdminBase?.selectedOrg?.orgID
                      ? sAdminBase?.selectedOrg?.orgID
                      : base.org.orgID;
                  // setOrgAtom(setOrgAtomState, itemList[0]);
                  navigate(`/admin/org/${orgID}`);
                }}
              >
                {isAdminPannel && sAdminBase?.selectedOrg?.orgID
                  ? sAdminBase?.selectedOrg?.name
                  : base.org.name}
              </Button>

              {isAdminPannel && (
                <Stack
                  direction="row"
                  my={1}
                  p={0}
                  sx={{
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      margin: "0px",
                      padding: "0px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Tooltip title="Add Folder">
                      <CreateNewFolderIcon
                        onClick={() => setSAdminBase(prev => ({...prev, addFolderModalOpen: true}))}
                        sx={{
                          color: "white",
                          marginRight: "10px",
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Add Cohort">
                      <FolderSharedIcon
                        onClick={() => setSAdminBase(prev => ({...prev, addCohortModalOpen: true}))}
                          sx={{
                            color: "white",
                            marginRight: "10px",
                            cursor: "pointer",
                          }}
                      />
                    </Tooltip>
                    <Tooltip title="Add Team">
                      <SupervisorAccountIcon
                      onClick={() => setSAdminBase(prev => ({...prev, addTeamModalOpen: true}))}
                        sx={{
                          color: "white",
                          marginRight: "10px",
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                  </Box>
                </Stack>
              )}
            </>
          ) : null}
          <List
            sx={{
              color: "white",
              width: "100%",
              paddingRight: "0px",
              marginRight: "0px",
              paddingLeft: isSuperAdmin
                ? "8px"
                : base.selectedGroup !== undefined
                ? "0px"
                : "8px",
            }}
          >
            {children}
          </List>
        </div>
      );
    };

    const getOrgMenuItems = () => {
        let buffer = [];

        for (let i = 0; i < orgTreeData?.orgs?.length; i++) {
            buffer.push({
                value: orgTreeData?.orgs[i].name,
                key: orgTreeData?.orgs[i].orgID
            })
        }

        buffer.sort((a, b) => {
            var nameA = a.value.toUpperCase(); // ignore upper and lowercase
            var nameB = b.value.toUpperCase(); // ignore upper and lowercase
            if (nameA < nameB) {
                return -1; //nameA comes first
            }
            if (nameA > nameB) {
                return 1; // nameB comes first
            }
            return 0;  // names must be equal
        });

        return buffer;
    }

    const renderOrgMenu = () => {
        let { org } = props,
            placeholder = (!org.id) ? { placeholder: 'No Organizations Yet!' } : {},
            orgItems = getOrgMenuItems();

        return <DropdownV2
            onChange={(_, org) => {
                if (base.org.orgID !== org && !isAdminPannel) {
                    setSessionByOrgID(
                        setDiscoverySession,
                        discoverySession,
                        org,
                        setBase,
                        setCategoryState,
                        setV3MigrationAtomState
                    );
                    setLoadingOrg(setBase, true);
                }
                else{
                    const selectedOrg = orgTreeData?.orgs?.find((orgItem) => orgItem?.orgID === org);
                    setSAdminBase((prev) => {
                        return {
                            ...prev,
                            selectedOrg,
                        };
                    });
                }
            }}

            selected={{ ...superAdminSelectedOrg }}
            {...placeholder}
            items={orgItems}
            menuWidth={'285px'}
            className={"iw-org-dropdown"}
            tooltip="Select Organization"
            theme={theme}
        />
    }

    const renderCohort = () => {
        const {
            group,
            children
        } = base.selectedGroup;
        const itemList = prepareHierarchy(children);
        const _children = [];

        if (children === undefined) return <div></div>

        for (let i = 0; i < itemList.length; i++) {
          if (itemList[i] && itemList[i].group) {
            if (itemList[i].group.name !== "-") {
              if (isSuperAdmin) {
                _children.push(
                    <div key={`cohort-${itemList[i].group.groupID}-${i}`}>
                    {renderHiearchyItem(
                        itemList[i].group,
                        itemList[i].itemList === undefined,
                        undefined
                    )}
                </div>
                );
              } else {

                let shouldReturn =
                  group?.visibility === "closed" || group === undefined
                    ? false
                    : true;
                const myRole = OrgRollSnapShot && OrgRollSnapShot[group?.groupID]

                if (
                  [
                    "Admin",
                    "CohortAdmin",
                    "Instructor",
                    "Observer",
                    "SystemAdministrator",
                  ].includes(myRole)
                ) {
                  shouldReturn = true;
                }

                orgTreeData?.roles?.forEach((role) => {
                  //    shouldReturn = base?.cohort?.visibility === "open" ? true : false;
                  if (
                    !shouldReturn &&
                    role.groupID === itemList[i]?.group?.groupID
                  ) {
                    shouldReturn = true;
                  }
                });

                if (shouldReturn) {
                  _children.push(
                    <div key={`cohort-${itemList[i].group.groupID}-${i}`}>
                    {
                    renderHiearchyItem(
                      itemList[i].group,
                      itemList[i].itemList === undefined,
                      undefined
                    )}
                    </div>
                  );
                }
              }
            }
          }
        }

        return (
            <div>
                {renderCurrentParent()}
                <List
                    sx={{
                        height: "100%",
                        overflowY: "auto",
                        padding: "8px",
                        paddingLeft: base.selectedGroup !== undefined ? "0px" : "8px",
                    }}
                >
                    {_children}
                </List>
            </div>
        );
    }

    const renderStickyToggle = () => {
        const isInInterviewPage = location.pathname?.includes("discovery");
        const orgTreeTooltip = (isInInterviewPage && windowSize <= INTERVIEW_PAGE_WIDTH_LIMITS.ORG_TREE)
            ? `To open the Organization Tree in sticky mood, browse the page in a wider screen (> ${INTERVIEW_PAGE_WIDTH_LIMITS.ORG_TREE}px).`
            : base?.orgTreeSticky ? "Unpin Menu" : "Pin Menu"

        return (
            <Tooltip title={orgTreeTooltip}>
                <IconButton
                    sx={{
                        color: "white",
                        ":hover": {
                            borderColor: `${theme?.custom?.mainButtonHighlight} !important`,
                            color: `${theme?.custom?.mainButtonHighlight} !important`,
                            transition: " transform ease-in-out",
                        },
                    }}
                    disabled={isInInterviewPage && windowSize <= INTERVIEW_PAGE_WIDTH_LIMITS.ORG_TREE}
                    onClick={() => toggleSticky(setBase, !base?.orgTreeSticky)}
                >
                    {base.orgTreeSticky ? (
                        <PushPinFilledIcon sx={PUSH_PIN_ICON_STYLES} />
                    ) : (
                        <PushPinOutlinedIcon sx={PUSH_PIN_ICON_STYLES} />
                    )}
                </IconButton>
            </Tooltip>
        );
    }

    const renderLightbulbMenuMaterial = () => {
        return (
            <Box onClick={() => navigate("/")}>
                <IwFullLogo
                    sx={{
                        display: "block",
                        cursor: "pointer",
                        color: theme?.custom?.white,
                        width: "150px",
                        height: "auto",
                    }}
                />
            </Box>
        );
    }

    const ratingsDistribution = useMemo(
        () =>
            isInScoringPage ? (
                <Box sx={{ padding: "16px 8px" }}>
                    <ScoringRatingPanel
                        ratingCriteria={Object.keys(STAR_RATINGS_DISTRIBUTION)}
                        ratings={Object.values(STAR_RATINGS_DISTRIBUTION)}
                        readOnly={true}
                        headerText="Star Ratings"
                        hideRatingChip={true}
                        sx={{
                            color: theme.custom.white,
                        }}
                        size="small"
                    />
                </Box>
            ) : null,
        [isInScoringPage]
    );

    if (base.orgTreeSticky) {
        return (
            base.app.orgTreeOpen && (
                <Box
                    className="material-org-tree"
                    sx={{
                        position: "relative",
                        minWidth: "300px",
                        maxWidth: "230px",
                        width: "300px",
                        zIndex: "1",
                        height: "100%",
                    }}
                >
                    <Box sx={parentPanelStyle}>
                        <Box  
                             sx={{
                                display: "flex",
                                border: "0px",
                                outline: "none",
                                px: "10px",
                            }}
                        >
                            {renderLightbulbMenuMaterial()}
                            <Stack sx={{ marginLeft: "auto" }}>
                                {/* <MaterialOrgTreeToggle
                                    disabled={isInScoringPage}
                                    tooltipText={renderOrgTreeToggleButtonTooltip()}
                                /> */}
                                <Box mt={"10px"}>
                                  {renderStickyToggle()}
                                </Box>
                            </Stack>
                        </Box>
                        <Box sx={{ px: "10px",pb:"10px",pt:"8px" }}>{renderOrgMenu()}</Box>
                        <Divider
                            sx={{
                                backgroundColor: "white",
                            }}
                        />
                        <Box
                            sx={{
                                padding: "16px 0",
                                color: "white",
                                overflowY: "auto",
                                height: "100%",
                                marginRight: "0px",
                                "::-webkit-scrollbar-thumb": {
                                    backgroundColor: "#69747f",
                                    "-webkit-border-radius": "1ex",
                                },
                            }}
                        >
                            {
                                (base.selectedGroup !== undefined && !isSuperAdmin)
                                ? renderCohort()
                                : renderGroupSelector()
                            }
                        </Box>
                        {ratingsDistribution}
                    </Box>
                </Box>
            )
        );
    }

    return (
        <React.Fragment key={"organization-navigator"}>
            <SwipeableDrawer
                anchor={"left"}
                variant={base.orgTreeSticky ? "permanent" : undefined}
                open={base.app.orgTreeOpen}
                onClose={() => {
                    setOrgToggle(setBase, false);
                }}
                onOpen={() => {
                    setOrgToggle(setBase, true);
                }}
                sx={{
                    ".MuiDrawer-paper": {
                        overflow: "hidden",
                        backgroundColor: theme.custom.darkestAccent,
                    },
                    zIndex: theme.custom.dialogZIndex,
                }}
            >
                <Box
                    className="material-org-tree"
                    sx={{
                        position: "relative",
                        width: "300px",
                        zIndex: "1",
                        height: "100%",
                    }}
                >
                    <Box sx={parentPanelStyle}>
                        <Box 
                            sx={{
                                display: "flex",
                                border: "0px",
                                outline: "none",
                                px: "10px",
                            }}
                        >
                            {renderLightbulbMenuMaterial()}
                            {/* <img id="app-logo" src="https://cdn.innovationwithin.services/logo/iw_logo_white.svg" /> */}
                            <Stack direction="row" alignItems="start" sx={{ marginLeft: "auto",mt:1}}>
                                <Box mt={"4px"}>
                                  {renderStickyToggle()}
                                </Box>
                            </Stack>
                        </Box>
                        <Box sx={{ padding: "10px" }}>{renderOrgMenu()}</Box>
                        <Divider
                            sx={{
                                backgroundColor: "white",
                            }}
                        />
                        <Box
                            sx={{
                                padding: "16px 0",
                                color: "white",
                                overflowY: "auto",
                                height: "100%",
                                marginRight: "0px",
                                "::-webkit-scrollbar-thumb": {
                                    backgroundColor: "#69747f",
                                    "-webkit-border-radius": "1ex",
                                },
                            }}
                        >
                            {
                                (base.selectedGroup !== undefined && !isSuperAdmin)
                                ? renderCohort() 
                                : renderGroupSelector()
                            }
                        </Box>
                        {ratingsDistribution}
                    </Box>
                </Box>
            </SwipeableDrawer>
        </React.Fragment>
    );

}
